import { getNotifications, markAllNotificationsRead, markNotificationRead } from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { ListRow, StatusBadge } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { formatDateTime } from '@/services/formatters';
import type { Notification } from '@/fixtures';

/**
 * Notification inbox with deep links.
 *
 * Each row navigates to the entity it is about, so an alert is actionable
 * rather than informational only. Unread state is carried by text and a badge,
 * never by colour alone.
 */

const KIND_LABEL: Readonly<Record<Notification['kind'], string>> = {
  payment: 'Payment',
  card: 'Card',
  risk: 'Security',
  approval: 'Approval',
  account: 'Account',
  system: 'Update',
};

const KIND_TONE: Readonly<Record<Notification['kind'], 'neutral' | 'info' | 'warning' | 'danger'>> = {
  payment: 'info',
  card: 'info',
  risk: 'danger',
  approval: 'warning',
  account: 'neutral',
  system: 'neutral',
};

export function NotificationsPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;

  const list = useAsync(async () => {
    if (!companyId) return [];
    return (await getNotifications(companyId)).data;
  }, [companyId]);

  const readOne = useMutation(async (id: string) => {
    if (!companyId) return [];
    return (await markNotificationRead(companyId, id)).data;
  });

  const readAll = useMutation(async () => {
    if (!companyId) return [];
    return (await markAllNotificationsRead(companyId)).data;
  });

  const unread = list.data?.filter((n) => !n.read).length ?? 0;

  return (
    <>
      <MobileHeader
        title="Notifications"
        backTo="/mobile/more"
        backLabel="More"
        trailing={
          unread > 0 ? (
            <button
              type="button"
              className="tm-btn tm-btn--quiet tm-btn--sm"
              onClick={() => {
                void readAll.run().then((outcome) => {
                  if (outcome.ok) list.setData(outcome.value);
                });
              }}
              disabled={readAll.pending}
            >
              Mark all read
            </button>
          ) : null
        }
      />

      <div className="tm-stack">
        {unread > 0 ? (
          <p className="tm-muted" role="status">
            {unread} unread notification{unread === 1 ? '' : 's'}
          </p>
        ) : null}

        {list.status === 'loading' ? (
          <LoadingRegion label="Loading notifications">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={4} />
            </div>
          </LoadingRegion>
        ) : null}

        {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}

        {list.status === 'success' && list.data.length === 0 ? (
          <EmptyState
            title="No notifications"
            body="Alerts about payments, cards and security will appear here."
          />
        ) : null}

        {list.status === 'success' && list.data.length > 0 ? (
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {list.data.map((n) => (
                <li key={n.id}>
                  <ListRow
                    to={n.route}
                    title={n.title}
                    meta={`${n.body} · ${formatDateTime(n.createdAt)}`}
                    end={
                      <span className="tm-inline" style={{ gap: 6 }}>
                        <StatusBadge status={n.kind} label={KIND_LABEL[n.kind]} tone={KIND_TONE[n.kind]} />
                        {!n.read ? (
                          <span className="tm-dot" aria-hidden="true" title="Unread" />
                        ) : null}
                      </span>
                    }
                    ariaLabel={`${n.read ? 'Read' : 'Unread'}. ${KIND_LABEL[n.kind]}. ${n.title}. ${n.body}. ${formatDateTime(n.createdAt)}`}
                  />
                  {!n.read ? (
                    <button
                      type="button"
                      className="tm-btn tm-btn--quiet tm-btn--sm"
                      style={{ marginInlineStart: 'auto', display: 'block' }}
                      onClick={() => {
                        void readOne.run(n.id).then((outcome) => {
                          if (outcome.ok) list.setData(outcome.value);
                        });
                      }}
                    >
                      Mark read
                      <span className="tm-visually-hidden">: {n.title}</span>
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </>
  );
}
